import { Button, Paper, Stack, TextField, Typography } from '@dtx-company/design-system/src'
import { Contacts } from '@mui/icons-material'
import {
  DEFAULT_DISPLAY_TYPE,
  FEATURED_DISPLAY_TYPE,
  ICONS_ONLY,
  IconSize,
  IconStyle
} from '../types'
import { DISPLAY_TYPE } from '../../Destination/constants'
import { DisplayToggle } from '../../components/DisplayToggle'
import { DragDropContext, Draggable, DraggableProvided, Droppable } from 'react-beautiful-dnd'
import { FC, useState } from 'react'
import { FormProps, StyledFormV2 } from '../../components/Layout'
import { IconEditor } from './components/IconEditor'
import { LinkStyleGated } from '../../components/LinkStyle/LinkStyleGated'
import { SettingsAndSaveLink } from '../../components/Settings'
import { SocialAdvLinkType, SocialLinkUrlFields } from '../../Destination/types'
import { SocialChip } from './components/SocialChip'
import { SocialLinkType } from '@dtx-company/inter-app/src/constants/linkTypes'
import { bwLogoUrlForProvider, getSocialInputPlaceholderText, reorderLinks } from './utils'
import { capitalize } from '@dtx-company/true-common/src/utils/strings'
import { useDraggableInPortal } from '../../../../utils/hooks'
import { useSocialLinkFormState } from '../hooks'
import { v4 as uuid } from 'uuid'
import Delete from '@mui/icons-material/Delete'
import DragHandle from '@mui/icons-material/DragHandle'
import Edit from '@mui/icons-material/Edit'
import FormatPaint from '@mui/icons-material/FormatPaint'
import Image from 'next/image'
import LinkOutlined from '@mui/icons-material/LinkOutlined'

export const SocialLinkFormV2: FC<FormProps> = ({ order, curr, handleClose }: FormProps) => {
  const [memoizedCurr] = useState(curr)
  const id = memoizedCurr ? memoizedCurr.id : uuid()
  const {
    watchAll,
    setValue,
    handleSubmit,
    onSubmit,
    updateSelectedLinks,
    selectedLinks,
    allowedSocialLinks,
    updateFormFieldValue,
    setSelectedLinks,
    errors,
    setLinkTheme,
    linkTheme,
    trigger,
    disabled,
    expandedChildLinks,
    setExpandedChildLinks
  } = useSocialLinkFormState(order, memoizedCurr, handleClose, id)
  const renderDraggable = useDraggableInPortal()
  const handleExpandChildlink = (provider: string): void => setExpandedChildLinks([provider])
  const handleDeleteChildlink = (provider: SocialLinkUrlFields): void =>
    updateSelectedLinks(provider)

  const displayIconEditors = watchAll.displayType === ICONS_ONLY
  return (
    <StyledFormV2 onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        <Stack gap={8}>
          <Stack gap={2}>
            <Stack direction="row" gap={2}>
              <Contacts />
              <Stack>
                <Typography variant="h4">Choose your social links</Typography>
                <Typography variant="labelMedium">
                  Select one or more socials to add to your page.
                </Typography>
              </Stack>
            </Stack>

            <Stack width="100%" direction="row" flexWrap="wrap" gap={2}>
              {allowedSocialLinks.map((linkType: SocialAdvLinkType) => (
                <SocialChip
                  provider={linkType.provider}
                  displayText={linkType.displayText ?? capitalize(linkType.provider)}
                  selected={selectedLinks.includes(linkType.provider)}
                  updateSelectedLinks={updateSelectedLinks}
                  key={`${linkType.provider}-social-chip`}
                />
              ))}
            </Stack>
          </Stack>

          <Stack gap={2}>
            <Stack direction="row" gap={2}>
              <FormatPaint />
              <Stack>
                <Typography variant="h4">Choose your link style</Typography>
                <Typography variant="labelMedium">
                  Select how you want your links to appear on your page.
                </Typography>
              </Stack>
            </Stack>
            <Stack gap={2}>
              <DisplayToggle
                title={'Link style'}
                displayOptions={[
                  {
                    title: 'Icon',
                    value: ICONS_ONLY
                  },
                  {
                    title: 'Card',
                    value: DEFAULT_DISPLAY_TYPE
                  },
                  {
                    title: 'Featured',
                    value: FEATURED_DISPLAY_TYPE
                  }
                ]}
                displayType={watchAll.displayType}
                onChange={(_e, type) => {
                  setValue(DISPLAY_TYPE, type)
                }}
              />

              {displayIconEditors && (
                <>
                  <DisplayToggle
                    title={'Icon size'}
                    displayOptions={[
                      { title: 'Standard', value: IconSize.MEDIUM },
                      { title: 'Large', value: IconSize.LARGE }
                    ]}
                    displayType={watchAll.iconSize}
                    onChange={(_e, type) => {
                      setValue('iconSize', type)
                    }}
                  />
                  <DisplayToggle
                    title={'Icon style'}
                    displayOptions={[
                      { title: 'Circle', value: IconStyle.CIRCLE },
                      { title: 'Floating', value: IconStyle.FLOATING }
                    ]}
                    displayType={watchAll.iconStyle}
                    onChange={(_e, type) => {
                      setValue('iconStyle', type)
                    }}
                  />
                </>
              )}
            </Stack>
          </Stack>

          <Stack gap={2}>
            <Stack direction="row" gap={2}>
              <LinkOutlined />
              <Stack>
                <Typography variant="h4">Edit your links</Typography>
                <Typography variant="labelMedium">Add your information to each link.</Typography>
              </Stack>
            </Stack>

            <DragDropContext
              onDragEnd={dropResult =>
                reorderLinks(
                  dropResult,
                  selectedLinks,
                  updateFormFieldValue,
                  watchAll,
                  setSelectedLinks
                )
              }
            >
              <Droppable droppableId="socialLinkList">
                {provided => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <Stack gap={4}>
                      {selectedLinks.map((linkType: string, index) => {
                        const socialLinkType = allowedSocialLinks.find(
                          (item: SocialLinkType) => item.provider === linkType
                        )

                        const provider = socialLinkType?.provider ?? ('' as SocialLinkUrlFields)
                        const showCardInputs = watchAll.displayType !== ICONS_ONLY

                        const expanded = socialLinkType?.provider
                          ? expandedChildLinks.includes(socialLinkType?.provider)
                          : false

                        return (
                          <Draggable
                            draggableId={`${index}-draggable`}
                            index={index}
                            key={`${linkType}-draggable`}
                          >
                            {renderDraggable((provided: DraggableProvided) => (
                              <div ref={provided.innerRef} {...provided.draggableProps}>
                                <Paper sx={{ p: 2 }}>
                                  <Stack justifyContent="space-between" width="100%">
                                    <Stack direction="row" gap={2} justifyContent="space-between">
                                      <Stack direction="row" alignItems="center" gap={2}>
                                        <Stack {...provided.dragHandleProps}>
                                          <DragHandle />
                                        </Stack>
                                        <Image
                                          src={bwLogoUrlForProvider(socialLinkType?.provider ?? '')}
                                          alt={`${socialLinkType?.provider || 'social'} icon`}
                                          width={24}
                                          height={24}
                                        />
                                        <Typography variant="h5">
                                          {socialLinkType?.displayText}
                                        </Typography>
                                      </Stack>

                                      <Stack alignItems="center" direction="row">
                                        {!expanded && (
                                          <Button
                                            size="small"
                                            variant="text"
                                            onClick={() =>
                                              handleExpandChildlink(socialLinkType?.provider ?? '')
                                            }
                                          >
                                            <Edit />
                                          </Button>
                                        )}
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() =>
                                            expanded
                                              ? handleDeleteChildlink(
                                                  socialLinkType?.provider as SocialLinkUrlFields
                                                )
                                              : handleExpandChildlink(
                                                  socialLinkType?.provider ?? ''
                                                )
                                          }
                                        >
                                          <Delete />
                                        </Button>
                                      </Stack>
                                    </Stack>
                                  </Stack>

                                  {expanded && (
                                    <Stack gap={2}>
                                      <TextField
                                        label="URL"
                                        hiddenLabel
                                        value={watchAll[provider]?.link || ''}
                                        error={!!errors[provider]?.link}
                                        helperText={errors[provider]?.message}
                                        placeholder={getSocialInputPlaceholderText(socialLinkType)}
                                        onBlur={() => trigger(`${provider}.link`)}
                                        onChange={e => {
                                          updateFormFieldValue(e.target.value, provider, 'link')
                                        }}
                                        startAdornment={
                                          socialLinkType?.isUsernameLink ? (
                                            <Stack justifyContent="center" alignContent="center">
                                              <Typography>
                                                {socialLinkType?.adornment || '@'}
                                              </Typography>
                                            </Stack>
                                          ) : undefined
                                        }
                                        fullWidth
                                      />

                                      <IconEditor
                                        provider={provider}
                                        imageUrl={watchAll[provider]?.thumbNailImgUrl ?? ''}
                                        setImage={(imageUrl: string) =>
                                          updateFormFieldValue(
                                            imageUrl,
                                            provider,
                                            'thumbNailImgUrl'
                                          )
                                        }
                                        featured={watchAll.displayType === FEATURED_DISPLAY_TYPE}
                                      />

                                      {showCardInputs && (
                                        <TextField
                                          label="Title"
                                          value={watchAll[provider]?.title ?? ''}
                                          error={!!errors[provider]?.title}
                                          placeholder="Enter a title"
                                          onBlur={() => trigger(`${provider}.title`)}
                                          onChange={e => {
                                            updateFormFieldValue(
                                              e.target.value,
                                              provider ?? '',
                                              'title'
                                            )
                                          }}
                                          fullWidth
                                        />
                                      )}
                                      {showCardInputs && (
                                        <TextField
                                          label="Description"
                                          value={watchAll[provider]?.description ?? ''}
                                          error={!!errors[provider]?.description}
                                          helperText={errors[provider]?.description?.message}
                                          placeholder="Enter a description"
                                          onBlur={() => trigger(`${provider}.description`)}
                                          onChange={e => {
                                            updateFormFieldValue(
                                              e.target.value,
                                              provider ?? '',
                                              'description'
                                            )
                                          }}
                                          fullWidth
                                        />
                                      )}
                                    </Stack>
                                  )}
                                </Paper>
                              </div>
                            ))}
                          </Draggable>
                        )
                      })}
                      {provided.placeholder}
                    </Stack>
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Stack>
        </Stack>
        <LinkStyleGated linkTheme={linkTheme} setLinkTheme={setLinkTheme} />

        <SettingsAndSaveLink disabled={disabled} curr={memoizedCurr} handleClose={handleClose} />
      </Stack>
    </StyledFormV2>
  )
}
